import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { 
  FaPhoneAlt, FaSearch, FaEnvelope, FaMapMarkerAlt, 
  FaTrash, FaTag, FaEye, FaTimes
} from 'react-icons/fa';
import '../pages_premium.css';
import './EnquiriesList.css';
import AdminPagination from '../components/common/AdminPagination';

export default function ContactusEnquiries() {
    const [enquiries, setEnquiries] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const fetchEnquiries = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/contact-enquiries');
            setEnquiries(res.data || []);
        } catch (error) {
            console.error('Error fetching contact enquiries:', error);
            toast.error('Failed to load contact enquiries');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnquiries();
    }, []);

    const deleteEnquiry = async (id) => {
        if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
        try {
            await axiosInstance.delete(`/contact-enquiries/${id}`);
            toast.success('Contact enquiry deleted successfully');
            if (selectedEnquiry && selectedEnquiry.id === id) {
                setSelectedEnquiry(null);
            }
            fetchEnquiries();
        } catch (error) {
            console.error('Error deleting enquiry:', error);
            toast.error('Failed to delete enquiry');
        }
    };

    const filtered = enquiries.filter(e =>
        e.name?.toLowerCase().includes(search.toLowerCase()) ||
        e.email?.toLowerCase().includes(search.toLowerCase()) ||
        e.phone?.toLowerCase().includes(search.toLowerCase()) ||
        e.subject?.toLowerCase().includes(search.toLowerCase()) ||
        e.address?.toLowerCase().includes(search.toLowerCase()) ||
        e.message?.toLowerCase().includes(search.toLowerCase())
    );

    const paginatedEnquiries = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const truncateMessage = (text, maxLength = 45) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div className="page-premium-wrapper enquiries-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaPhoneAlt size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">Contact Us Enquiries</h1>
                            <p className="ph-subtitle">Manage all general contact enquiries submitted via footer & contact page</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{enquiries.length}</span>
                            <span className="count-lbl">Total Submissions</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Contact Form Submissions
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by name, email, phone, address, subject..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="ptable-loading">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted font-weight-bold" style={{ fontSize: '13px' }}>
                                Loading contact enquiries...
                            </p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="ptable-empty">
                            <FaEnvelope size={42} className="mb-3 text-muted" />
                            <h5>No Contact Enquiries Found</h5>
                            <p className="text-muted small">
                                {search ? 'No results match your search query.' : 'No contact form enquiries have been submitted yet.'}
                            </p>
                        </div>
                    ) : (
                        <div className="table-responsive" style={{ overflowX: 'auto' }}>
                            <table className="table ptable align-middle mb-0" style={{ minWidth: '1250px', borderCollapse: 'separate', borderSpacing: 0, border: '1px solid #e2e8f0' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                        <th style={{ width: '60px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>#ID</th>
                                        <th style={{ minWidth: '160px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Name</th>
                                        <th style={{ minWidth: '190px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Email</th>
                                        <th style={{ minWidth: '140px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Mobile</th>
                                        <th style={{ minWidth: '150px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Subject</th>
                                        <th style={{ minWidth: '160px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Address</th>
                                        <th style={{ minWidth: '220px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Message</th>
                                        <th style={{ minWidth: '180px', padding: '14px 16px', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Submitted On</th>
                                        <th className="text-center" style={{ minWidth: '110px', padding: '14px 16px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedEnquiries.map((item) => (
                                        <tr key={item.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <span className="badge badge-light border text-dark font-weight-bold px-2 py-1">
                                                    #{item.id}
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <div className="d-flex align-items-center">
                                                    <div className="user-avatar-circle mr-2.5 bg-primary text-white d-flex align-items-center justify-content-center font-weight-bold rounded-circle shadow-sm" style={{ width: '36px', height: '36px', fontSize: '13px', flexShrink: 0 }}>
                                                        {item.name ? item.name.charAt(0).toUpperCase() : 'U'}
                                                    </div>
                                                    <div className="font-weight-bold text-dark" style={{ fontSize: '14px' }}>
                                                        {item.name}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <div className="text-dark small font-weight-semibold">
                                                    <FaEnvelope className="mr-1.5 text-primary" size={11} />
                                                    <a href={`mailto:${item.email}`} className="text-primary text-decoration-none font-weight-bold">
                                                        {item.email}
                                                    </a>
                                                </div>
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                {item.phone ? (
                                                    <div className="text-dark small font-weight-bold">
                                                        <FaPhoneAlt className="mr-1.5 text-success" size={11} />
                                                        {item.phone}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted small italic">N/A</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <span className="badge badge-info px-2.5 py-1.5 font-weight-bold" style={{ fontSize: '12px', borderRadius: '6px' }}>
                                                    <FaTag className="mr-1" size={10} />
                                                    {item.subject || 'General'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                {item.address ? (
                                                    <div className="text-dark small font-weight-semibold">
                                                        <FaMapMarkerAlt className="mr-1 text-danger" size={11} />
                                                        {item.address}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted small italic">N/A</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <div className="text-dark small font-weight-medium" style={{ lineHeight: '1.4' }}>
                                                    {truncateMessage(item.message, 45)}
                                                </div>
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                                <span className="text-muted small font-weight-bold" style={{ whiteSpace: 'nowrap' }}>
                                                    {new Date(item.createdAt).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </td>
                                            <td className="text-center" style={{ padding: '14px 16px' }}>
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <button
                                                        className="btn btn-info text-white shadow-sm mr-2"
                                                        style={{ width: '34px', height: '34px', borderRadius: '50%', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                        onClick={() => setSelectedEnquiry(item)}
                                                        title="View Details"
                                                    >
                                                        <FaEye size={15} />
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-danger shadow-sm"
                                                        style={{ width: '34px', height: '34px', borderRadius: '50%', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                        onClick={() => deleteEnquiry(item.id)}
                                                        title="Delete Enquiry"
                                                    >
                                                        <FaTrash size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="enquiries"
                    />
                </div>
            </div>

            {/* ── ATTRACTIVE ENQUIRY DETAILS MODAL POPUP ─────────────────────── */}
            {selectedEnquiry && (
                <div 
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Escape') setSelectedEnquiry(null); }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.65)',
                        backdropFilter: 'blur(6px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1.5rem'
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) setSelectedEnquiry(null); }}
                >
                    <div 
                        role="dialog"
                        aria-modal="true"
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '24px',
                            maxWidth: '650px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            border: '1px solid #f1f5f9'
                        }}
                    >
                        {/* Modal Header */}
                        <div 
                            style={{
                                padding: '1.25rem 1.5rem',
                                borderBottom: '1px solid #f1f5f9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                                borderTopLeftRadius: '24px',
                                borderTopRightRadius: '24px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <div 
                                    className="mr-3"
                                    style={{
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '14px',
                                        backgroundColor: '#e0f2fe',
                                        color: '#0284c7',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <FaEnvelope size={20} />
                                </div>
                                <div>
                                    <div className="d-flex align-items-center">
                                        <h5 className="mb-0 font-weight-bold text-dark mr-2" style={{ fontSize: '18px' }}>
                                            Contact Enquiry Details
                                        </h5>
                                        <span className="badge badge-primary px-2 py-1 font-weight-bold" style={{ fontSize: '11px', borderRadius: '6px' }}>
                                            #{selectedEnquiry.id}
                                        </span>
                                    </div>
                                    <p className="text-muted mb-0 small font-weight-semibold">
                                        Submitted on {new Date(selectedEnquiry.createdAt).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setSelectedEnquiry(null)}
                                style={{
                                    border: 'none',
                                    background: '#f1f5f9',
                                    color: '#64748b',
                                    width: '34px',
                                    height: '34px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <FaTimes size={16} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '1.5rem' }}>
                            {/* User Header Info Card */}
                            <div 
                                style={{
                                    backgroundColor: '#f8fafc',
                                    borderRadius: '16px',
                                    padding: '1.25rem',
                                    border: '1px solid #e2e8f0',
                                    marginBottom: '1.25rem'
                                }}
                            >
                                <div className="d-flex align-items-center mb-3">
                                    <div 
                                        className="mr-3"
                                        style={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '50%',
                                            backgroundColor: '#3b82f6',
                                            color: '#ffffff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '18px',
                                            boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)'
                                        }}
                                    >
                                        {selectedEnquiry.name ? selectedEnquiry.name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <div>
                                        <h6 className="mb-1 font-weight-bold text-dark" style={{ fontSize: '16px' }}>
                                            {selectedEnquiry.name}
                                        </h6>
                                        <span className="badge badge-info px-2.5 py-1 font-weight-bold" style={{ fontSize: '11px' }}>
                                            <FaTag className="mr-1" size={9} />
                                            Subject: {selectedEnquiry.subject || 'General Enquiry'}
                                        </span>
                                    </div>
                                </div>

                                <div className="row" style={{ fontSize: '13px' }}>
                                    <div className="col-sm-6 mb-2">
                                        <span className="text-muted d-block small font-weight-bold text-uppercase">Email Address</span>
                                        <span className="text-dark font-weight-bold">
                                            <FaEnvelope className="mr-1.5 text-primary" size={12} />
                                            <a href={`mailto:${selectedEnquiry.email}`} className="text-primary text-decoration-none">
                                                {selectedEnquiry.email}
                                            </a>
                                        </span>
                                    </div>
                                    <div className="col-sm-6 mb-2">
                                        <span className="text-muted d-block small font-weight-bold text-uppercase">Phone Number</span>
                                        <span className="text-dark font-weight-bold">
                                            <FaPhoneAlt className="mr-1.5 text-success" size={12} />
                                            {selectedEnquiry.phone || 'Not Provided'}
                                        </span>
                                    </div>
                                    <div className="col-12 mt-1">
                                        <span className="text-muted d-block small font-weight-bold text-uppercase">Address / Location</span>
                                        <span className="text-dark font-weight-bold">
                                            <FaMapMarkerAlt className="mr-1.5 text-danger" size={12} />
                                            {selectedEnquiry.address || 'Not Provided'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Full Message Box */}
                            <div>
                                <h6 className="font-weight-bold text-dark mb-2" style={{ fontSize: '14px' }}>
                                    Full Message Content:
                                </h6>
                                <div 
                                    style={{
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '16px',
                                        padding: '1.25rem',
                                        color: '#1e293b',
                                        fontSize: '14px',
                                        lineHeight: '1.6',
                                        whiteSpace: 'pre-wrap',
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.03)'
                                    }}
                                >
                                    {selectedEnquiry.message}
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer Actions */}
                        <div 
                            style={{
                                padding: '1rem 1.5rem',
                                borderTop: '1px solid #f1f5f9',
                                backgroundColor: '#f8fafc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottomLeftRadius: '24px',
                                borderBottomRightRadius: '24px'
                            }}
                        >
                            <button
                                className="btn btn-outline-danger font-weight-bold btn-sm px-3 py-2"
                                style={{ borderRadius: '10px' }}
                                onClick={() => deleteEnquiry(selectedEnquiry.id)}
                            >
                                <FaTrash className="mr-1" /> Delete Enquiry
                            </button>
                            <button
                                className="btn btn-secondary font-weight-bold btn-sm px-4 py-2"
                                style={{ borderRadius: '10px' }}
                                onClick={() => setSelectedEnquiry(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
